import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AddPeopleTo from '../../components/AddPeopleTo';
import Breadcrumbs from '../../components/Breadcrumbs';
import { OutlineButton } from '../../components/Buttons';
import EasyLinkModal from '../../components/EasyLinkModal';
import LoadingState from '../../components/LoadingState';
import { Org } from '../org';
import { groupService } from '../../services/groupService';
import orgService from '../../services/orgService';
import windowService from '../../services/windowService';
import { handleError } from '../../utils/apiUtils';
import { logger } from '../../utils/logger';
import { useUser } from '../authentication';
import EmptyState from './EmptyState';
import GroupContainer from './GroupContainer';

type Group = {
  id: string;
  groupName: string;
  groupLink: string;
  groupUsers: GroupUser[];
  pendingMembers: GroupUser[];
};

type GroupUser = {
  userId?: { id: string };
  inviteId?: { id: string };
};

type OrgUser = {
  _id?: { id: string };
  inviteId?: { id: string };
  groups: string[];
};

type UrlParams = {
  groupId: string;
  orgId: string;
};

const GroupAddPeople = () => {
  const { user } = useUser();
  const { groupId, orgId } = useParams<UrlParams>();
  const [status, setStatus] = useState({ isLoading: true, isError: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [group, setGroup] = useState<Group | undefined>(undefined);
  const [people, setPeople] = useState<OrgUser[]>([]);
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [showEasyLink, setShowEasyLink] = useState(false);
  const [crumbs, setCrumbs] = useState<{ name: string; route?: string }[]>([{ name: 'Groups', route: '#/groups' }]);

  useEffect(() => {
    if (!user?.userId || !groupId || !orgId) {
      setStatus({ isError: true, isLoading: false });
    }

    const getGroup = groupService.getSingleGroup(groupId, orgId);
    const getOrgUsers = orgService.getOrgUsers(orgId);
    const getOrg = orgService.getOrg(orgId);

    (Promise.all([getGroup, getOrgUsers, getOrg]) as Promise<[Group, { users: OrgUser[] }, Org]>)
      .then(([groupResponse, orgUsersResponse, orgResponse]: [Group, { users: OrgUser[] }, Org]) => {
        const groupName = groupResponse.groupName;
        setCrumbs(prevCrumbs => [
          ...prevCrumbs,
          { name: groupName, route: `#/groups/${groupId}/${orgId}` },
          { name: 'Add People' },
        ]);

        setGroup(groupResponse);
        setPeople(orgUsersResponse.users);

        const roles =
          orgResponse.roles
            ?.filter(r => r.active === true)
            .map(r => ({
              id: r.id.id,
              name: r.name,
            })) || [];

        const otherGroups =
          orgResponse.groups
            ?.filter(g => g.id.id !== groupId && g.published)
            .map(g => ({
              id: g.id.id,
              name: g.name,
            })) || [];

        setFilters({
          roles: { displayName: 'Roles', items: roles },
          groups: { displayName: 'Groups', items: otherGroups },
        });

        setStatus({ isLoading: false, isError: false });
      })
      .catch(error => {
        logger.error('Error fetching group or org info:', error);
        setStatus({ isError: true, isLoading: false });
      });
  }, [groupId, orgId]);

  const handleAddPeople = (selectedPeople: unknown) => {
    if (!user?.userId) return handleError(new Error('User not found'));
    if (!group?.groupName) return handleError(new Error('Group name not found'));

    setIsSubmitting(true);
    groupService
      .addMember(groupId, orgId, user.userId, selectedPeople, group.groupName)
      .then(() => windowService.redirectTo(`/groups/${groupId}/${orgId}`))
      .catch((error: Error) => {
        handleError(error);
        setIsSubmitting(false);
      });
  };

  return status.isError || !user ? (
    <EmptyState isError={'is-error'} />
  ) : (
    <>
      <Breadcrumbs crumbs={crumbs} />
      <GroupContainer data-qa-hook="addPeopleView" rounded>
        <header className="space-between">
          <div>
            <h1>Add People to Group</h1>
            <p className="group-desc">
              Select people from your organization to add or use the Group Easy Link button to share a link people can
              use to join this group.
            </p>
          </div>
          <div>
            <OutlineButton
              data-qa-hook="EasyLinkModalButton"
              disabled={status.isLoading}
              onClick={() => setShowEasyLink(true)}
            >
              <span className="fas fa-share-square"></span> Group Easy Link
            </OutlineButton>
            {showEasyLink && group && (
              <EasyLinkModal
                qrCodeIdentifier={`Add People to ${group.groupName}`}
                link={group.groupLink || ''}
                description={`<h4>Anyone who uses this link will automatically be added to <b>${group.groupName}</b>.</h4>`}
                handleDismiss={() => setShowEasyLink(false)}
              />
            )}
          </div>
        </header>
        <section>
          {status.isLoading ? (
            <LoadingState />
          ) : (
            <AddPeopleTo
              user={user}
              isSubmitting={!!isSubmitting}
              people={people}
              alreadyAddedPeople={people.filter(u => !u.groups.includes(groupId))}
              handleAddPeople={handleAddPeople}
              filters={filters}
            />
          )}
        </section>
      </GroupContainer>
    </>
  );
};

export default GroupAddPeople;
